import React from "react";
import { useTranslation } from "react-i18next";
import { PastureView } from "../../../hooks/usePaddockMapColors";
import { formatDateToCustomFormat } from "../../../utils/date/formatedDate";
import { BatchesSpecsDetails } from "../BatchesSpecsDetails";
import { getLocalizedApiName } from "../../../utils/i18n/getLocalizedApiName";

type MicroAreaTooltipProps = {
  microArea: IMicroArea;
  view: PastureView;
  batchLabel?: string;
  qtdLabel?: string;
  capacity?: string;
  acreage?: string;
  maxDescanso?: number;
  maxOcupacao?: number;
};

export function MicroAreaTooltip({ 
  microArea, 
  view, 
  batchLabel, 
  qtdLabel, 
  capacity, 
  acreage,
  maxDescanso,
  maxOcupacao
}: MicroAreaTooltipProps) {
  const { i18n } = useTranslation();

  const shouldShowDetails = () => {
    switch (view) {
      case 'capacity-immediate':
      case 'capacity-average':
        // Para capacity, não verificar management - usar apenas o valor de lotação
        return !!(microArea.average_lot_rate || microArea.instant_lot_rate);
      case 'paddock-time':
      case 'default-map':
        return true;
      default:
        // Para outras visualizações, verificar management
        if (!microArea.management?.type?.id || microArea.management.type.id === 1) return false;
        
        switch (view) {
          case 'management-type':
            return true;
          case 'management-height':
            return microArea.management?.type?.monitoring_types?.some(type => type.id === 2);
          case 'management-rate':
            return microArea.management?.type?.monitoring_types?.some(type => type.id === 1);
          case 'management-day':
            return microArea.management?.type?.monitoring_types?.some(type => type.id === 3);
          default:
            return false;
        }
    }
  };

  const getClassName = () => {
    return shouldShowDetails() ? "" : "";
  };

  const renderManagementDetails = () => {
    if (!shouldShowDetails()) return null;

    return (
      <div className="infoSpecsDetails__container">

        {/* Tipo de manejo */}
        {view === 'management-type' && (
          <h3 className="qtdDetails" style={{ marginTop: 10 }}>
            {getLocalizedApiName(microArea.management?.type?.monitoring_types?.[0]?.name, i18n.language)}
          </h3>
        )}

        {/* Nota */}
        {view === 'management-rate' && microArea.management?.type?.monitoring_types?.some(type => type.id === 1) && (
          <div style={{ marginTop: 10 }}>
            <h3 className="qtdDetails">
              Nota de entrada: {microArea.management?.requirement_in}
              <br />
              Nota de saída: {microArea.management?.requirement_out}
              <div className="delimiter" style={{ marginTop: 10, marginBottom: 10 }} />
              {microArea.monitory_values?.length > 0 ? (
                (() => {
                  const monitoringValue = microArea.monitory_values?.find(value => value.monitoring_type_id.id === 1);
                  return monitoringValue ? (
                    <>
                      Última nota coletada: <strong>{monitoringValue.value}</strong>
                      <br />
                      {formatDateToCustomFormat(
                        new Date(monitoringValue.updated_at || monitoringValue.created_at)
                      )}
                    </>
                  ) : (
                    <>
                      Nota atual
                      <br />
                      não coletada
                    </>
                  );
                })()
              ) : (
                <>
                  Nota atual
                  <br />
                  não coletada
                </>
              )}
            </h3>
          </div>
        )}

        {/* Altura */}
        {view === 'management-height' && microArea.management?.type?.monitoring_types?.some(type => type.id === 2) && (
          <div style={{ marginTop: 10 }}>
            <h3 className="qtdDetails">
              Altura de entrada: {microArea.management?.requirement_in}
              <br />
              Altura de saída: {microArea.management?.requirement_out}
              <div className="delimiter" style={{ marginTop: 10, marginBottom: 10 }} />
              {microArea.monitory_values?.length > 0 ? (
                (() => {
                  const monitoringValue = microArea.monitory_values?.find(value => value.monitoring_type_id.id === 2);
                  return monitoringValue ? (
                    <>
                      Última altura coletada: <strong>{monitoringValue.value}</strong>
                      <br />
                      {formatDateToCustomFormat(
                        new Date(monitoringValue.updated_at || monitoringValue.created_at)
                      )}
                    </>
                  ) : (
                    <>
                      Altura atual
                      <br />
                      não coletada
                    </>
                  );
                })()
              ) : (
                <>
                  Altura atual
                  <br />
                  não coletada
                </>
              )}
            </h3>
          </div>
        )}

        {/* Dias */}
        {view === 'management-day' && microArea.management?.type?.monitoring_types?.some(type => type.id === 3) && (
          <div style={{ marginTop: 10 }}>
            <h3 className="qtdDetails">
              Dias de ocupação: {microArea.management?.requirement_in}
              <br />
              Dias de descanso: {microArea.management?.requirement_out}
              <div className="delimiter" style={{ marginTop: 10, marginBottom: 10 }} />
              {microArea.days_grazing !== 0 ? (
                <div>
                  {microArea.days_grazing} dias
                  <br />
                  de ocupação
                </div>
              ) : (
                <div>
                  {microArea.days_without_grazing} dias
                  <br />
                  de descanso
                </div>
              )}
            </h3>
          </div>
        )}

        {/* Capacidade */}
        {(view === 'capacity-immediate' || view === 'capacity-average') && (
          <BatchesSpecsDetails
            batchLabel={batchLabel}
            qtdLabel={qtdLabel}
            capacity={capacity}
            acreage={acreage}
          />
        )}

        {/* Paddock Time */}
        {view === 'paddock-time' && (
          <div>
            <h3 className="qtdDetails">
            {microArea.days_grazing !== 0 ? (
                maxOcupacao !== undefined && (
                  <>
                    <div style={{ marginTop: 10 }} >
                      Máximo de dias
                      <br />
                      de ocupação: {maxOcupacao}
                    </div>
                  </>
                )
              ) : (
                maxDescanso !== undefined && (
                  <>
                    <div style={{ marginTop: 10}} >
                      Máximo de dias
                      <br />
                      de descanso: {maxDescanso}
                    </div>
                  </>
                )
              )}
              <div className="delimiter" style={{ marginTop: 10, marginBottom: 10 }} />
              {microArea.days_grazing !== 0 ? (
                <div>
                  {microArea.days_grazing} dias
                  <br />
                  de ocupação
                </div>
              ) : (
                <div>
                  {microArea.days_without_grazing} dias
                  <br />
                  de descanso
                </div>
              )}
            </h3>
          </div>
        )}

        {/* Default Map - Informações básicas */}
        {view === 'default-map' && (
          <BatchesSpecsDetails
            batchLabel={batchLabel}
            qtdLabel={qtdLabel}
            capacity={capacity}
            acreage={acreage}
          />
        )}
      </div>
    );
  };

  return (
    <div className={getClassName() } style={{ marginLeft: 10, marginRight: 10 }}>
      {renderManagementDetails()}
    </div>
  );
}
